// Single Solid store for global state. Components reach in via the named
// signals/getters; mutations go through the explicit `actions` API so that
// surface area stays inspectable.

import type { Project, ProjectRole, Remark } from '@ladd-screenshot/shared';
import { createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { api, restoreAuthToken, setAuthToken } from '../api/client';
import { connectWs, onWsEvent, subscribeProject, unsubscribeProject } from '../ws/client';

export interface AuthState {
  email: string | null;
  expiresAt: number | null;
}

export interface TargetWithCounts {
  id: string;
  projectId: string;
  slug: string;
  path: string;
  fullPage: number;
  triggerLazyLoad: number;
  excludeVariants: string[] | null;
  interactions: unknown;
  config: unknown;
  capturesCount: number;
  remarksCount: number;
}

export interface CaptureRow {
  id: string;
  projectId: string;
  variantId: string;
  targetId: string;
  resolutionName: string;
  filePath: string;
  sha256: string;
  capturedAt: number;
  status: string;
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
}

export type ViewMode =
  | 'kanban'
  | 'compare'
  | 'slider'
  | 'overlay'
  | 'diff'
  | 'history'
  | 'last';

export type ZoomFitMode = 'fitWidth' | 'fitHeight' | 'auto' | null;

export type ToastKind = 'info' | 'success' | 'error';

export interface ToastMessage {
  id: number;
  kind: ToastKind;
  message: string;
  duration?: number;
}

export interface AppState {
  projects: Project[];
  activeProjectId: string | null;
  activeProjectRole: ProjectRole | null;
  remarks: Remark[];
  targets: TargetWithCounts[];
  activeTargetId: string | null;
  captures: CaptureRow[];
  activeResolution: string | null;
  viewMode: ViewMode;
  activeRemarkId: string | null;
  // Phase 10B/C — toolbar interaction state
  zoomPercent: number;
  zoomFitMode: ZoomFitMode;
  annotateMode: boolean;
  remarksVisible: boolean;
  // Legacy version selector for OLD column: 'current' (no selection),
  // 'old' (production baseline), or a history-version timestamp.
  leftVersion: string;
  // Phase 10B — UI feedback for AddRemark drag-create flow
  pendingRect: { x: number; y: number; width: number; height: number; captureId: string; variantId: string; resolutionName: string } | null;
  // Sprint 2 / 10F polish — sidebar collapse + toast notification
  sidebarCollapsed: boolean;
  toast: ToastMessage | null;
  bootLoading: boolean;
  capturesLoading: boolean;
  remarksLoading: boolean;
}

const SIDEBAR_COLLAPSED_KEY = 'ladd-screenshot.sidebarCollapsed';
function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

const [auth, setAuth] = createStore<AuthState>({ email: null, expiresAt: null });
const [state, setState] = createStore<AppState>({
  projects: [],
  activeProjectId: null,
  activeProjectRole: null,
  remarks: [],
  targets: [],
  activeTargetId: null,
  captures: [],
  activeResolution: null,
  viewMode: 'last',
  activeRemarkId: null,
  zoomPercent: 100,
  zoomFitMode: 'auto',
  annotateMode: false,
  remarksVisible: true,
  pendingRect: null,
  leftVersion: 'current',
  sidebarCollapsed: readSidebarCollapsed(),
  toast: null,
  bootLoading: true,
  capturesLoading: false,
  remarksLoading: false,
});

let nextToastId = 1;
const [bootError, setBootError] = createSignal<string | null>(null);
const [turnstileSiteKey, setTurnstileSiteKey] = createSignal<string | null>(null);
const [turnstileEnabled, setTurnstileEnabled] = createSignal<boolean>(true);

export const stores = { auth, state };
export const bootErrorSignal = bootError;
export const turnstileSiteKeySignal = turnstileSiteKey;
export const turnstileEnabledSignal = turnstileEnabled;

export const actions = {
  async boot() {
    try {
      const cfg = await api.getConfig();
      setTurnstileEnabled(cfg.turnstileEnabled);
      setTurnstileSiteKey(cfg.turnstileSiteKey ?? null);
    } catch (err) {
      setBootError(err instanceof Error ? err.message : 'config load failed');
      setState('bootLoading', false);
      return;
    }
    const token = restoreAuthToken();
    if (!token) {
      setState('bootLoading', false);
      return;
    }
    try {
      const verified = await api.verify();
      setAuth({ email: verified.email, expiresAt: verified.expiresAt });
      await actions.loadProjects();
      connectWs();
      onWsEvent((evt) => {
        if (evt.projectId !== state.activeProjectId) return;
        // alpha.9 — single-remark patch in local state (no full reloadRemarks
        // round-trip). Created prepends, updated swaps in place, deleted
        // filters out. Falls back to a full reload on unexpected shapes.
        if (evt.type === 'remark.created') {
          setState('remarks', [evt.remark, ...state.remarks]);
        } else if (evt.type === 'remark.updated') {
          setState(
            'remarks',
            state.remarks.map((r) => (r.id === evt.remark.id ? evt.remark : r)),
          );
        } else if (evt.type === 'remark.deleted') {
          setState(
            'remarks',
            state.remarks.filter((r) => r.id !== evt.remarkId),
          );
        }
      });
    } catch {
      setAuthToken(null);
    } finally {
      setState('bootLoading', false);
    }
  },

  async login(email: string, password: string, turnstileToken: string | null = null): Promise<void> {
    const payload: { email: string; password: string; turnstileToken?: string } = { email, password };
    if (turnstileToken) payload.turnstileToken = turnstileToken;

    let res: Awaited<ReturnType<typeof api.login>>;
    try {
      res = await api.login(payload);
    } catch (err) {
      console.error('[login] api.login threw', err);
      throw err;
    }

    if (!res || typeof res.token !== 'string' || !res.token) {
      console.error('[login] unexpected response shape, missing token', res);
      throw new Error('Login response missing token');
    }

    // Belt-and-suspenders: write localStorage directly in addition to the
    // module-level setAuthToken() so a stale closure / bundling quirk on the
    // shared mutable `authToken` ref can never leave the browser without a
    // persisted token. alpha.10.1 fix for the audit that saw localStorage
    // empty post-submit despite a 200 backend response.
    try {
      localStorage.setItem('ladd-screenshot.token', res.token);
    } catch (err) {
      console.error('[login] localStorage.setItem failed', err);
    }
    setAuthToken(res.token);
    setAuth({ email: res.email, expiresAt: res.expiresAt });

    // loadProjects + connectWs may individually fail without invalidating the
    // login itself — guard each so a downstream hiccup doesn't roll back the
    // visible "logged in" state the user already earned.
    try {
      await actions.loadProjects();
    } catch (err) {
      console.warn('[login] loadProjects failed post-auth', err);
    }
    try {
      connectWs();
    } catch (err) {
      console.warn('[login] connectWs failed post-auth', err);
    }
  },

  async logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      setAuthToken(null);
      setAuth({ email: null, expiresAt: null });
      setState({ projects: [], activeProjectId: null, activeProjectRole: null, remarks: [] });
    }
  },

  async loadProjects(): Promise<void> {
    const res = await api.listProjects();
    setState('projects', res.projects);
    if (!state.activeProjectId && res.projects.length > 0) {
      await actions.selectProject(res.projects[0]!.id);
    }
  },

  async selectProject(projectId: string): Promise<void> {
    if (state.activeProjectId === projectId) return;
    if (state.activeProjectId) unsubscribeProject(state.activeProjectId);
    const detail = await api.getProject(projectId);
    setState({
      activeProjectId: detail.project.id,
      activeProjectRole: detail.role,
      remarks: [],
      targets: [],
      activeTargetId: null,
      captures: [],
      activeResolution: null,
    });
    subscribeProject(projectId);
    await Promise.all([
      actions.reloadRemarks(),
      actions.loadTargets(),
      actions.loadAllCaptures(),
    ]);
  },

  // Load every capture of the active project (no targetId filter). Used at
  // boot so the sidebar can show the "last updated" timestamp based on the
  // most recent capturedAt, even before the user picks a target. Legacy
  // parity: stbarts reads metadata.json globally at boot.
  async loadAllCaptures(): Promise<void> {
    const projectId = state.activeProjectId;
    if (!projectId) return;
    try {
      const res = await api.listCaptures(projectId);
      setState('captures', res.captures as unknown as CaptureRow[]);
    } catch {
      // ignore — sidebar will fall back to empty timestamp
    }
  },

  async loadTargets(): Promise<void> {
    const projectId = state.activeProjectId;
    if (!projectId) {
      setState('targets', []);
      return;
    }
    try {
      const res = await api.listTargets(projectId);
      setState('targets', res.targets);
    } catch {
      setState('targets', []);
    }
  },

  async selectTarget(targetId: string | null): Promise<void> {
    if (state.activeTargetId === targetId) return;
    setState({ activeTargetId: targetId, captures: [], activeResolution: null });
    if (targetId) await actions.loadCaptures(targetId);
  },

  async loadCaptures(targetId: string): Promise<void> {
    const projectId = state.activeProjectId;
    if (!projectId) {
      setState('captures', []);
      return;
    }
    setState('capturesLoading', true);
    try {
      const res = await api.listCaptures(projectId, targetId);
      const rows = res.captures as unknown as CaptureRow[];
      setState('captures', rows);
      const firstResolution =
        rows.find((c) => c.resolutionName)?.resolutionName ?? null;
      setState('activeResolution', firstResolution);
      // Auto-promote leftVersion to 'old' when a baseline capture exists —
      // mirrors legacy renderContent() L2932-2938 so first render shows the
      // OLD panel populated, not the "Select a version" placeholder.
      const hasOld = rows.some((c) => c.variantId === 'old');
      if (state.leftVersion === 'current' && hasOld) {
        setState('leftVersion', 'old');
      } else if (state.leftVersion === 'old' && !hasOld) {
        setState('leftVersion', 'current');
      }
    } catch {
      setState('captures', []);
    } finally {
      setState('capturesLoading', false);
    }
  },

  setLeftVersion(version: string): void {
    setState('leftVersion', version);
  },

  setActiveResolution(name: string): void {
    setState('activeResolution', name);
  },

  setViewMode(mode: ViewMode): void {
    setState('viewMode', mode);
  },

  setZoom(percent: number): void {
    setState('zoomPercent', Math.max(10, Math.min(400, Math.round(percent))));
    setState('zoomFitMode', null);
  },

  setZoomFitMode(mode: ZoomFitMode): void {
    setState('zoomFitMode', mode);
  },

  toggleAnnotateMode(): void {
    setState('annotateMode', !state.annotateMode);
  },

  toggleRemarksVisible(): void {
    setState('remarksVisible', !state.remarksVisible);
  },

  setPendingRect(rect: AppState['pendingRect']): void {
    setState('pendingRect', rect);
  },

  setSidebarCollapsed(collapsed: boolean): void {
    setState('sidebarCollapsed', collapsed);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // localStorage may be unavailable (private mode) — collapsed state stays
      // in-memory only for this session.
    }
  },

  toggleSidebar(): void {
    actions.setSidebarCollapsed(!state.sidebarCollapsed);
  },

  pushToast(message: string, kind: ToastKind = 'info', duration?: number): void {
    setState('toast', { id: nextToastId++, kind, message, duration });
  },

  dismissToast(): void {
    setState('toast', null);
  },

  setBootLoading(loading: boolean): void {
    setState('bootLoading', loading);
  },

  openRemarkDetail(remarkId: string): void {
    setState('activeRemarkId', remarkId);
  },

  closeRemarkDetail(): void {
    setState('activeRemarkId', null);
  },

  async reloadRemarks(): Promise<void> {
    const projectId = state.activeProjectId;
    if (!projectId) {
      setState('remarks', []);
      return;
    }
    setState('remarksLoading', true);
    try {
      const res = await api.listRemarks(projectId);
      setState('remarks', res.remarks);
    } catch {
      setState('remarks', []);
    } finally {
      setState('remarksLoading', false);
    }
  },

  async createRemark(
    body: Parameters<typeof api.createRemark>[1],
  ): Promise<void> {
    const projectId = state.activeProjectId;
    if (!projectId) throw new Error('No active project');
    await api.createRemark(projectId, body);
    // WS event will trigger reloadRemarks. Optimistic refresh as fallback.
    await actions.reloadRemarks();
  },

  async updateRemark(
    remarkId: string,
    patch: Parameters<typeof api.updateRemark>[1],
  ): Promise<void> {
    const updated = await api.updateRemark(remarkId, patch);
    setState(
      'remarks',
      state.remarks.map((r) => (r.id === remarkId ? updated : r)),
    );
  },

  async deleteRemark(remarkId: string): Promise<void> {
    await api.deleteRemark(remarkId);
    setState(
      'remarks',
      state.remarks.filter((r) => r.id !== remarkId),
    );
  },

  async addComment(
    remarkId: string,
    body: Parameters<typeof api.addComment>[1],
  ): Promise<void> {
    await api.addComment(remarkId, body);
    await actions.reloadRemarks();
  },
};
